import { nextTick } from '@patrianna/shared-utils'
import { intlnumberFormat } from '@patrianna/shared-utils/numbers'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { buyShopOffer } from '@patrianna-payments/shared-store/payments/store/middleware-actions'
import cx from 'classnames'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'

import Button from 'components/Button'
import Img from 'components/Img'
import RichText from 'components/RichText'
import { IS_BRAND_PULSZ } from 'config/brands'
import content from 'prismic-translates/common.json'
import { trackSPPlacementInteracted } from 'src/analyticActions/common/trackPaymentSnowplow'
import { useAppDispatch } from 'src/store/hooks'

import OfferOldPrice from './components/OfferOldPrice'
import OfferTagsHeader from './components/OfferTagsHeader'
import OfferTagsWrapper from './components/OfferTagsWrapper'
import classes from './styles/smallStyles.module.scss'
import type { ShopTileProps } from './types'

const t = getTranslationFromJson([content])

function ShopTileSmall(props: ShopTileProps) {
  const { hideBuyBtn } = props
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { offer, loggedIn, snowplowPayFlow, snowplowPlacementName } = props
  const scActualPrice = offer.sweepstakeFirstOffer || offer.sweepstakeMoney
  const gcActualPrice = offer.goldFistOffer || offer.goldMoney
  const [showFullBenefitsInfo, setShowFullBenefitsInfo] = useState(false)
  const showFreeSpins = !!offer.freeSpins
  const scBenefits = Boolean(scActualPrice && props.sweepstakeEnabled)
  const infoForLogoutAndGcUsers = Boolean(!loggedIn || !props.sweepstakeEnabled)
  const specialOfferImgUrl = offer.specialOfferUrl || `${process.env.IMG_ORIGIN}/shop/coins-v2.png`
  const offerTags = offer?.tags

  useEffect(() => {
    if (infoForLogoutAndGcUsers) {
      setShowFullBenefitsInfo(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tagsString = useMemo(() => offerTags?.join(', '), [offerTags])

  const handleBuy = () => {
    nextTick().then(() => {
      trackSPPlacementInteracted({
        paymentFlow: snowplowPayFlow,
        placementName: snowplowPlacementName,
        offer,
        interactionName: intlnumberFormat(offer.price, 'en-US', { style: 'currency', currency: 'USD' }),
        placementTrigger: 'click_store_button',
      })
      dispatch(
        buyShopOffer({
          offer,
          feature: 'in_store',
          routerPush: router.push,
          snowplowPayFlow,
        }),
      )
    })
  }

  const handleButtonClick = () => {
    handleBuy()
    props?.trackButtonClick?.()
  }

  return (
    <OfferTagsWrapper enabled={!!offerTags.length}>
      <div
        className={cx(classes.root, props.disableHover && classes.disabledHover, props.classNames, 'mt-paper')}
        data-tags={tagsString}
        data-testid={props.dataTestId || 'store-offer-item'}
      >
        <OfferTagsHeader tags={offerTags} />
        <div className={cx(classes.content, props.insufficientFundsView && classes.insufficientContent)}>
          <div className={cx(props.insufficientFundsView && classes.insufficientFundsWrapper)}>
            <div className={classes.col}>
              <picture>
                <Img
                  src={specialOfferImgUrl}
                  className={cx(classes.icon, props.insufficientFundsView && classes.insufficientCoin)}
                  alt={offer.title}
                  fallbackImg={specialOfferImgUrl}
                />
              </picture>
              <div className={classes.coins}>
                <p className={classes.newCount} data-testid='store-offer-gc-amount'>
                  <b>
                    {t('gold_coins_gc')} {intlnumberFormat(gcActualPrice)}
                  </b>
                </p>
                {offer.goldFistOffer && !props.insufficientFundsView && (
                  <p className={classes.oldCount}>
                    {t('was')}
                    <span className={classes.disabledText}>{intlnumberFormat(offer.goldMoney)}</span>
                  </p>
                )}
              </div>
            </div>
            {props.insufficientFundsView && !!scActualPrice && (
              <div className={cx(classes.col)}>
                <RichText
                  field={t('free_sc_subtitle')}
                  components={{
                    strong: () => (
                      <>
                        {t('sweepstakes_coins_sc')}{' '}
                        {intlnumberFormat(scActualPrice, 'en-US', {
                          minimumFractionDigits: 2,
                        })}
                      </>
                    ),
                    paragraph: ({ children }) => <b className={classes.scBlockUnderGC}>{children}</b>,
                    em: () => (
                      <img
                        src={`${process.env.IMG_ORIGIN}/sc-icon.png`}
                        className={classes.currencyIcon}
                        width={20}
                        height={14}
                        alt='sweepstakes icon'
                      />
                    ),
                  }}
                />
              </div>
            )}
          </div>
          {!hideBuyBtn && (
            <div className={classes.col}>
              <Button
                dataTestId={`mobile-pay-button-${offer.code}`}
                onClick={handleButtonClick}
                className={classes.button}
                disabled={props.disabled}
              >
                {intlnumberFormat(offer.price, 'en-US', { style: 'currency', currency: 'USD' })}
                <OfferOldPrice oldPrice={offer.oldPrice} />
              </Button>
            </div>
          )}
        </div>

        {!props.insufficientFundsView && (
          <div className={classes.footer}>
            <div className={classes.benefitsTitle}>Free {showFullBenefitsInfo && 'Purchase'} Benefits</div>

            {offer.vipPoints && (
              <div className={cx(classes.vipCoin, { [classes.points]: IS_BRAND_PULSZ })}>
                <span className={classes.vipCoinValue}>
                  {intlnumberFormat(offer.vipPoints, 'en-US', { style: 'decimal' })}
                </span>
                <br />
                {IS_BRAND_PULSZ ? t('pulsz_points') : 'VIP'}
              </div>
            )}

            <div className={classes.goldenKey}>
              <img width={18} height={18} src={`${process.env.IMG_ORIGIN}/icons/key.png`} alt='key' />
              <span>Key</span>
            </div>

            {scBenefits ? (
              <div className={classes.sweepBonus} data-testid='store-offer-sc-amount'>
                <span className={cx(!showFullBenefitsInfo ? classes.sweepBonusValue : classes.sweepBonusValueColumn)}>
                  <img
                    src={`${process.env.IMG_ORIGIN}/icons/sc-icon.png`}
                    className={classes.currencyIcon}
                    alt='SC icon'
                  />
                  <span>
                    SC&nbsp;
                    {intlnumberFormat(scActualPrice, 'en-US', {
                      style: 'decimal',
                      minimumFractionDigits: scActualPrice % 1 ? 2 : 0,
                    })}
                  </span>
                </span>
              </div>
            ) : (
              <span />
            )}

            {showFreeSpins ? (
              <div className={classes.freeSpins}>
                <img width={19} height={16} src={`${process.env.IMG_ORIGIN}/icons/free-spins.png`} alt='free spins' />
                <span>
                  {offer.freeSpins} {t('free_spins_short')}
                </span>
              </div>
            ) : (
              <span />
            )}
          </div>
        )}
      </div>
    </OfferTagsWrapper>
  )
}

export default ShopTileSmall
